// https://df4.microgsd.net:60891/chsc/gold-engraved-medallion-certificate-frame-78276.aspx?ep_type=3&ep_size=1
import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {DefaultPCS, EPBorderType, EPMethod, EPSizeType, EP_DEFAULT_FIXED_SIZE} from '../../rendering/render.def';
import ModalSimple from '../../../global/modal-simple';
import InfoIcon from '../shared/info-icon';
import {Portal} from '../../../util';

@observer
export default class EngravedPlate extends React.Component {
    @observable ep1: string = '';
    @observable ep2: string = '';
    @observable ep3: string = '';
    @observable ep4: string = '';
    @observable ep5: string = '';
    @observable ep6: string = '';
    @observable ep7: string = '';
    @observable ep8: string = '';
    @observable ep9: string = '';
    @observable ep10: string = '';
    @observable collapsed: boolean = true;

    constructor(props) {
        super(props);
        this.collapseToggle = this.collapseToggle.bind(this);
        makeObservable(this);
        this.seedLinesFromProductState();
    }

    // These fields are the only writers of s.ps.ep1..ep10 while the card is mounted, so plate text
    // that was restored into s.ps before mount (share link, cart Edit Frame) has to be copied back
    // in. Otherwise the inputs render blank and the next ep_change_text wipes the restored text.
    seedLinesFromProductState = () => {
        const ps = s.ps;
        if (!ps) return;

        this.ep1 = ps.ep1 || '';
        this.ep2 = ps.ep2 || '';
        this.ep3 = ps.ep3 || '';
        this.ep4 = ps.ep4 || '';
        this.ep5 = ps.ep5 || '';
        this.ep6 = ps.ep6 || '';
        this.ep7 = ps.ep7 || '';
        this.ep8 = ps.ep8 || '';
        this.ep9 = ps.ep9 || '';
        this.ep10 = ps.ep10 || '';
    };

    componentDidMount() {
        if (s.ui.ep_mode_expanded) {
            s.ui.ep_mode_expanded = false;
            const el = document.getElementById('ep-inner');
            if (el) el.classList.add('show');
        }
        this.syncEpColorSelect();
    }

    componentDidUpdate() {
        this.syncEpColorSelect();
    }

    syncEpColorSelect = () => {
        const select = document.getElementById('ep-color-select') as HTMLSelectElement;
        if (!select) return;
        const target = String(s.ps.ep_id_view);
        const options = Array.from(select.options).map(o => o.value);
        if (options.includes(target)) {
            select.value = target;
        } else if (options.length > 0) {
            select.value = options[0];
            s.ps.ep_id_view = parseInt(options[0], 10);
            if (s.ps.ep_id > 0) {
                s.ps.ep_id = s.ps.ep_id_view;
            }
        }
    };

    @action
    collapseToggle = async () => {
        this.collapsed = !this.collapsed;
    };

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    @action
    switchToCustom = () => {
        window.dataLayer.push({event: 'ep_adv'});
        s.ui.ep_mode_expanded = true;
        s.prod.EPMethodID = EPMethod.Custom;
        s.ps.EPMethodID = EPMethod.Custom;
        s.prod.EPBorderType = EPBorderType.Inset;
        s.ps.EPBorderType = EPBorderType.Inset;
        s.prod.EPSizeType = EPSizeType.AutoSnap;
        s.ps.EPSizeType = EPSizeType.AutoSnap;
        s.prod.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
        s.prod.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
        s.ps.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
        s.ps.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
        DefaultPCS.DefaultEPWidth = EP_DEFAULT_FIXED_SIZE.width;
        DefaultPCS.DefaultEPHeight = EP_DEFAULT_FIXED_SIZE.height;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    @action
    ep_color_change = async (e: any) => {
        s.ps.ep_id_view = parseInt(e.target.value, 10);
        if (s.ps.ep_id > 0) {
            s.ps.ep_id = s.ps.ep_id_view;
            s.ui.doRender = true;
            s.ui.update_pricing = true;
            s.pulse();
        }
    };

    @action
    ep_add = async () => {
        s.ps.ep_id = s.ps.ep_id_view;
        s.ui.doRender = true;
        s.ps.ep1 = this.ep1;
        s.ps.ep2 = this.ep2;
        s.ps.ep3 = this.ep3;
        s.ps.ep4 = this.ep4;
        s.ps.ep5 = this.ep5;
        s.ps.ep6 = this.ep6;
        s.ps.ep7 = this.ep7;
        s.ps.ep8 = this.ep8;
        s.ps.ep9 = this.ep9;
        s.ps.ep10 = this.ep10;
        s.ps.lino_base_name = '';
        s.ps.lino_id = -1;
        s.ui.update_pricing = true;
        s.pulse();

        if (!s.prod.IsCAF) {
            if (s.ps.ep_id === 1099) {
                s.ps.ins_black_allowed = true;
                s.ps.ins_gold_allowed = true;
                s.ps.ins_silver_allowed = true;
            } else if (s.ps.ep_id === 39) {
                s.ps.ins_black_allowed = false;
                s.ps.ins_gold_allowed = false;
                s.ps.ins_silver_allowed = true;
            } else if (s.ps.ep_id === 37) {
                s.ps.ins_black_allowed = false;
                s.ps.ins_gold_allowed = true;
                s.ps.ins_silver_allowed = false;
            }
        }

        s.enforce_color_selections_caf();
    };

    @action
    ep_remove = async (e: any) => {
        if (s.ps.ep_id > 0) {
            s.ps.ep_id = 0;
            s.ui.update_pricing = true;
            s.ui.doRender = true;
            s.pulse();
        }
    };

    @action
    ep_change_text = async () => {
        s.ps.ep1 = this.ep1;
        s.ps.ep2 = this.ep2;
        s.ps.ep3 = this.ep3;
        s.ps.ep4 = this.ep4;
        s.ps.ep5 = this.ep5;
        s.ps.ep6 = this.ep6;
        s.ps.ep7 = this.ep7;
        s.ps.ep8 = this.ep8;
        s.ps.ep9 = this.ep9;
        s.ps.ep10 = this.ep10;
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    @computed get getColorText(): string {
        switch (s.ps.ep_id) {
            case 37:
                return `Gold`;
            case 39:
                return `Silver`;
            case 1099:
                return `Black`;
            case 33146:
                return `White`;

            default:
                return '';
        }
    }

    @computed get getColorTextLower(): string {
        if (s.ui.name_moulding.toLowerCase().includes('silver') || !s.ps.ins_gold_allowed) {
            switch (s.ps.ep_id_view) {
                case 37:
                    return `gold`;
                case 39:
                    return `silver`;
                case 1099:
                    return `black`;
                case 33146:
                    return `white`;

                default:
                    return 'silver';
            }
        } else {
            switch (s.ps.ep_id_view) {
                case 37:
                    return `gold`;
                case 39:
                    return `silver`;
                case 1099:
                    return `black`;
                case 33146:
                    return `white`;

                default:
                    return 'gold';
            }
        }
    }

    @computed get getImage(): string {
        switch (s.ps.ep_id) {
            case 37:
                return `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_gold_original_engravedplate.png`;
            case 39:
                return `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_silver_original_engravedplate.png`;
            case 1099:
                return `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_black_original_engravedplate.png`;
            case 33146:
                return `https://${Portal?.CDNDomain}/images/cached/general/ep_white_original_engravedplate.png`;

            default:
                break;
        }

        return `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_gold_original_engravedplate.png`;
    }

    render() {
        if (!s.ui.show_cust || !s.prod.EngravedPlateStatusID || s.prod.EngravedPlateStatusID < 1) {
            return;
        }

        return (
            <div
                className="gray-box ep"
                style={{padding: 0}}
            >
                <ModalSimple
                    id="modal-sample-ep"
                    title="Personalized Plate Information"
                    maxWidth="600px"
                >
                    <div>
                        {s.ps.EPMethodID <= EPMethod.Extended && (
                            <ul>
                                {s.prod.EngravedPlateStatusID === 0 && <li>This product does not allow a personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 1 && <li>This product requires a gold personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 2 && <li>This product requires a silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 3 && <li>This product allows a gold personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 4 && <li>This product allows a silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 5 && <li>This product allows a gold or silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 6 && <li>This product requires a black personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 7 && <li>This product allows a black personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 8 && <li>This product requires a white personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 9 && <li>This product allows a white personalized plate</li>}
                                <li>Enter up to 30 characters per line</li>
                                <li>You may have 1, 2, or 3 lines centered on the personalized plate</li>
                                <li>Dimensions: 3" X 1"</li>
                                <li>If you do not provide text, a plate will not be included with your order</li>
                            </ul>
                        )}

                        {s.ps.EPMethodID > EPMethod.Extended && (
                            <ul>
                                <li>This product allows a gold or silver personalized plate</li>
                                <li>Choose one or multiple fonts and font sizes to create your custom plate</li>
                                <li>Dimensions: (dynamic)</li>
                                <li>Depending on the plate size, you can have multiple lines of text. If you add too many characters to a line or too many lines for that plate size, you will be notified</li>
                                <li>If you do not provide text, a plate will not be included with your order</li>
                            </ul>
                        )}

                        <div>
                            <b>Example Personalized plate:</b>
                        </div>
                        <img
                            src={`https://${Portal?.CDNDomain}/images/products/full/80049pid25402.png`}
                            alt="Example personalized plate"
                            aria-label="Example personalized plate"
                        ></img>
                    </div>
                </ModalSimple>
                <div className={this.canCollapse && s.ps.is_caf ? '' : ''}>
                    <div
                        style={{
                            paddingLeft: 12,
                            paddingRight: 12,
                            paddingTop: 0,
                            paddingBottom: 0,
                            position: 'relative',
                        }}
                    >
                        <div
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-block toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>Personalized Plate: </span>
                                <span>{this.getColorText}</span>
                            </div>
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-block d-xl-none toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        Personalized Plate
                                    </div>
                                    <div className="cust-flex2">{this.getColorText}</div>
                                </div>
                                <div>
                                    {s.ps.ep_id > 0 && (
                                        <img
                                            className="th-h"
                                            src={this.getImage}
                                            alt="Personalized Plate Thumbnail"
                                            style={{height: 48, width: 'auto'}}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="ep-inner"
                        className={this.canCollapse ? 'collapse' : ''}
                        style={{
                            padding: '0 12px',
                        }}
                    >
                        <div>
                            <div className={`ep-container ${this.getColorTextLower}`}>
                                {s.prod.EngravedPlateLines > 0 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 1 of the personalized plate"
                                            placeholder="Line 1"
                                            autoComplete="off"
                                            id="ep-1"
                                            value={this.ep1}
                                            onChange={e => {
                                                this.ep1 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 1 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 2 of the personalized plate"
                                            placeholder="Line 2"
                                            autoComplete="off"
                                            id="ep-2"
                                            value={this.ep2}
                                            onChange={e => {
                                                this.ep2 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 2 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 3 of the personalized plate"
                                            placeholder="Line 3"
                                            autoComplete="off"
                                            id="ep-3"
                                            value={this.ep3}
                                            onChange={e => {
                                                this.ep3 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 3 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 4 of the personalized plate"
                                            placeholder="Line 4"
                                            autoComplete="off"
                                            id="ep-4"
                                            value={this.ep4}
                                            onChange={e => {
                                                this.ep4 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 4 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 5 of the personalized plate"
                                            placeholder="Line 5"
                                            autoComplete="off"
                                            id="ep-5"
                                            value={this.ep5}
                                            onChange={e => {
                                                this.ep5 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 5 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 6 of the personalized plate"
                                            placeholder="Line 6"
                                            autoComplete="off"
                                            id="ep-6"
                                            value={this.ep6}
                                            onChange={e => {
                                                this.ep6 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 6 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 7 of the personalized plate"
                                            placeholder="Line 7"
                                            autoComplete="off"
                                            id="ep-7"
                                            value={this.ep7}
                                            onChange={e => {
                                                this.ep7 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 7 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 8 of the personalized plate"
                                            placeholder="Line 8"
                                            autoComplete="off"
                                            id="ep-8"
                                            value={this.ep8}
                                            onChange={e => {
                                                this.ep8 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 8 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 9 of the personalized plate"
                                            placeholder="Line 9"
                                            autoComplete="off"
                                            id="ep-9"
                                            value={this.ep9}
                                            onChange={e => {
                                                this.ep9 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                                {s.prod.EngravedPlateLines > 9 && (
                                    <div>
                                        <input
                                            type="text"
                                            className="ep-text"
                                            maxLength={30}
                                            aria-label="Line 10 of the personalized plate"
                                            placeholder="Line 10"
                                            autoComplete="off"
                                            id="ep-10"
                                            value={this.ep10}
                                            onChange={e => {
                                                this.ep10 = e.target.value;
                                                this.ep_change_text();
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="ep-option">
                                {s.prod.IsCAF && (Portal?.PortalID === 0 || Portal?.PortalID === 1 || Portal?.PortalID === 2 || !s.is_production) && (
                                    <div className="ep-mode-switch">
                                        <button
                                            type="button"
                                            aria-label="Switch to custom personalized plate editor"
                                            onClick={this.switchToCustom}
                                        >
                                            Customize
                                        </button>
                                    </div>
                                )}
                                {s.prod.EngravedPlateStatusID === 5 && (
                                    <div className="ep-color">
                                        <select
                                            id="ep-color-select"
                                            name="ep-color-select"
                                            onChange={this.ep_color_change}
                                            aria-label="Select personalized plate color"
                                        >
                                            {s.ps.ins_gold_allowed && <option value="37">Gold</option>}
                                            {s.ps.ins_silver_allowed && s.ps.mat2_id !== 385 && <option value="39">Silver</option>}
                                        </select>
                                    </div>
                                )}
                                {s.prod.EngravedPlateStatusID !== 1 && s.prod.EngravedPlateStatusID !== 2 && s.prod.EngravedPlateStatusID !== 6 && s.prod.EngravedPlateStatusID !== 8 && (
                                    <div className="ep-toggle">
                                        {(!s.ps.ep_id || s.ps.ep_id <= 0) && (
                                            <button
                                                type="button"
                                                aria-label="Add personalized plate to product"
                                                onClick={this.ep_add}
                                            >
                                                Add Plate to Frame
                                            </button>
                                        )}
                                        {s.ps.ep_id > 0 && (
                                            <button
                                                type="button"
                                                aria-label="Remove personalized plate from product"
                                                onClick={this.ep_remove}
                                            >
                                                Remove Plate
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="ep-link mb-1">
                                View information about personalized plates
                                <InfoIcon
                                    id="modal-sample-ep"
                                    ariaLabel="View more information about personalized plates"
                                    useMargin={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
